import { z } from 'zod'
import {
  AppVisibilityState,
  BaseEvent,
  IAnalyticsSdk,
  RuntimeContextProvider,
  Validator,
  ExtraOptions,
  CommonsEventDetail,
} from '@mmt/events-schema'
import { SessionManager } from './global-state/session'
import { OptInStateManager } from './global-state/opt-in-status'
import { newCorrelationId, newRequestId } from './global-state/id-generators'
import { IJobQueue } from './job-queue'
import { Org, OrgValue } from '../constants'
import { appSettingsInstance } from './global-state/global-data'
import { syncSessionIdToPdtDb } from '../platforms/browser/browser-session-validation'
import { getCampaignDetails, getEntityInfo, getUserUUIDPt } from '../platforms/browser/utils'
import { ADLSchemaImpl } from './adobe-analytics/adobe-analytics-sdk'
import { baseSchemaValidation, formatZodError } from './validation/validator'
export interface AnalyticsConfig {
  assertSchema: boolean
  autoFlushIntervalInMs: number
  maxBatchSize: number
}

export interface MmtAnalyticsOptions<T> {
  sessionManager: SessionManager
  optInStateManager: OptInStateManager
  runtimeContextProvider: RuntimeContextProvider | undefined
  sdkRuntimeProvider: RuntimeContextProvider
  jobQueue: IJobQueue<T>
  org: Org
}

// FIXME use DI library tsyringe [https://github.com/microsoft/tsyringe]
export class AnalyticsSdk<T extends CommonsEventDetail> implements IAnalyticsSdk<T> {
  private readonly jobQueue: IJobQueue<T>
  private readonly sessionManager: SessionManager
  private readonly optInStateManager: OptInStateManager
  private runtimeContextProvider: RuntimeContextProvider | undefined
  private sdkRuntimeProvider: RuntimeContextProvider
  private validators: Array<Validator<T>> = []
  public org: Org

  constructor(options: MmtAnalyticsOptions<T>) {
    const { sessionManager, optInStateManager, runtimeContextProvider, jobQueue, sdkRuntimeProvider, org } = options
    this.runtimeContextProvider = runtimeContextProvider
    this.sdkRuntimeProvider = sdkRuntimeProvider
    this.sessionManager = sessionManager
    this.optInStateManager = optInStateManager
    this.jobQueue = jobQueue
    this.jobQueue.start()
    this.org = org || OrgValue.MMT
  }

  newCorrelationId = (): Promise<string> => Promise.resolve(newCorrelationId())
  newRequestId = (): Promise<string> => Promise.resolve(newRequestId())
  getSessionId = (): Promise<string> => Promise.resolve(this.sessionManager.getCurrentSessionId())
  setOptOutStatus = (status: boolean) => this.optInStateManager.updateOptInStatus(status)
  getOptOutStatus = () => this.optInStateManager.readOptInStatus()
  track = async (event: BaseEvent<T>, lob: string) => {
    const eventPayload = await this.enrichBaseEventArg(event, lob)
    for await (const validator of this.validators) {
      await validator.assertValidSchema(eventPayload, lob)
    }
    this.jobQueue.pushEvent(eventPayload)
    this.sessionManager.updateLastUsed()
  }

  pushToPDT = async (event: BaseEvent<T>, extraOptions?: ExtraOptions) => {
    const lob = extraOptions?.lob || ''
    const eventPayload = await this.enrichAndValidateBaseEvent(event, extraOptions)

    for await (const validator of this.validators) {
      await validator.assertValidSchema(eventPayload, lob)
    }
    this.jobQueue.pushEvent(eventPayload)
    this.sessionManager.updateLastUsed()
  }

  pushToAdobeDataLayer = async (schema: BaseEvent<T>, extraOptions?: ExtraOptions): Promise<void> => {
    const enrichedBaseEvent = await this.enrichAndValidateBaseEvent(schema, extraOptions)
    new ADLSchemaImpl(enrichedBaseEvent, extraOptions)
  }

  pushToAll = async (schema: BaseEvent<T>, extraOptions?: ExtraOptions) => {
    await Promise.all([this.pushToPDT(schema), this.pushToAdobeDataLayer(schema, extraOptions)])
  }

  flushEvents = () => {
    this.jobQueue.flushNow('terminal')
  }

  stopAutoFlush = () => {
    this.flushEvents()
    this.jobQueue.shutdown()
  }

  onAppVisibilityChange = (visibility: AppVisibilityState) => {
    if (visibility === 'hidden') {
      this.flushEvents()
    }
  }

  addValidator(validator: Validator<T>): void {
    this.validators.push(validator)
  }

  setRuntimeContextProvider(runtimeInfoProvider: RuntimeContextProvider) {
    this.runtimeContextProvider = runtimeInfoProvider
  }

  private enrichBaseEventArg = async (evArg: BaseEvent<T>, lob: string): Promise<BaseEvent<T>> => {
    const sessionId = await this.getSessionId()
    const eventId = await this.newRequestId()
    const deviceContext = this.runtimeContextProvider?.getDeviceContext()
    const userContext = this.runtimeContextProvider?.getUserContext(this.org)
    const getExtendedUserContext = this.runtimeContextProvider?.getUserDetailsContextExtended?.()
    const getExtendedDeviceDetailsContext = this.runtimeContextProvider?.getDeviceDetailsContextExtended?.()
    const sdkDeviceContext = this.sdkRuntimeProvider?.getDeviceContext()
    const sdkUserContext = this.sdkRuntimeProvider?.getUserContext(this.org)
    const sdkEventTrackingContext = this.sdkRuntimeProvider?.getEventTrackingContext?.()
    const getCMPDetails = getCampaignDetails()
    const entityInfo = getEntityInfo()
    const identities = this.sdkRuntimeProvider?.getUserIdentities?.(this.org)
    const pageContext = this.sdkRuntimeProvider?.getPageContext?.(this.org)

    appSettingsInstance.setSetting('deviceContext', deviceContext)

    if (userContext?.uuid && userContext?.profile_type) {
      const userIdPT = getUserUUIDPt({ uuid: userContext?.uuid, profile_type: userContext?.profile_type })
      appSettingsInstance.setSetting('userIdPt', userIdPT)
    }
    const getRetryData = await this.sessionManager.getRetryStatus()
    const getSyncData = await this.sessionManager.getSyncStatus()
    if (!getSyncData && getRetryData > 0 && getRetryData < 6) {
      setTimeout(async () => {
        await syncSessionIdToPdtDb(true, this.sessionManager, undefined)
      }, 10)
    }

    const finalEventPayload = Object.freeze<BaseEvent<T>>({
      ...evArg,
      page_context: {
        ...pageContext,
        ...evArg?.page_context,
      },
      identities: {
        ...identities,
        ...evArg?.identities,
      },
      event_detail: {
        // @ts-expect-error event_id is overwritten but this is intended
        event_id: eventId,
        // @ts-expect-error event_timestamp is overwritten but this is intended
        event_timestamp: Date.now(),
        device_details_extended: {
          ...getExtendedDeviceDetailsContext,
        },
        user_details_extended: {
          ...getExtendedUserContext,
        },
        ...evArg?.event_detail,
      },
      event_tracking_context: {
        ...sdkEventTrackingContext,
        ...evArg?.event_tracking_context,
        session_id: sessionId,
      },
      user_context: {
        ...sdkUserContext,
        ...userContext,
        ...evArg?.user_context,
      },
      device_context: {
        ...sdkDeviceContext,
        ...deviceContext,
        ...evArg?.device_context,
      },
      campaign_details: evArg?.campaign_details || getCMPDetails,
      entity_info: entityInfo,
    })

    const validationPromises = this.validators.map(validator => validator.assertValidSchema(finalEventPayload, lob))
    await Promise.all(validationPromises)

    return finalEventPayload
  }

  private async enrichAndValidateBaseEvent(evArg: BaseEvent<T>, extraOptions?: ExtraOptions): Promise<BaseEvent<T>> {
    const lob = extraOptions?.lob || ''
    const enrichedEvent = await this.enrichBaseEventArg(evArg, lob)
    try {
      baseSchemaValidation.parse(enrichedEvent)
    } catch (error) {
      if (error instanceof z.ZodError) {
        const formattedError = formatZodError(error)
        // eslint-disable-next-line no-console
        console.error('Event Schema:', enrichedEvent)
        throw new Error(`Schema Validation Failed: ${JSON.stringify(formattedError, null, 2)}`)
      }
      throw error
    }

    return enrichedEvent
  }
}
